import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { DEFAULT_PROVIDER_TYPE, PROVIDER_TYPES, PROVIDER_TYPE_LABELS, getProviderTypeLabel, isValidProviderType } from '../config/providerTypes';
import FiltersSidebar from '../components/FiltersSidebar';
import HairstylistList from '../components/HairstylistList';
import HairstylistsMap from '../components/HairstylistsMap';
import SearchableCountrySelect from '../components/SearchableCountrySelect';
import useHairstylists from '../hooks/useHairstylists';
import { useHairstylistProfile } from '../hooks/useHairstylistProfile';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { db } from '../firebase';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import styles from './SearchPage.module.css';

const CREATE_CHECKOUT_SESSION_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/createCheckoutSession';
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_REPLACE_ME';

const FILTERS_STORAGE_KEY = 'searchPageFilters';
const SORT_STORAGE_KEY = 'searchPageSort';
const VIEW_STORAGE_KEY = 'searchPageView';
const SCROLL_STORAGE_KEY = 'searchPageScrollY';
const MARKER_STORAGE_KEY = 'searchPageMarkerSelection';

function getDefaultCountryForLanguage(language) {
  return language === 'de' ? 'de' : 'be';
}

function getDefaultFilters(language = 'fr') {
  return {
    searchText: '',
    selectedServices: [],
    selectedCategories: [],
    selectedCity: '',
    selectedCities: [], // Villes multiples pour les filtres
    selectedCountry: getDefaultCountryForLanguage(language),
    kidsAccepted: false,
    menAccepted: false,
    canTravel: false,
    hasPhotos: false,
  };
}

function SearchPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { currentUser } = useAuth();
  const { isHairstylist, hairstylistProfile } = useHairstylistProfile();
  const isPendingPayment = isHairstylist && hairstylistProfile?.paymentStatus === 'pending';
  const [activatingProfile, setActivatingProfile] = useState(false);

  const handleActivateProfile = async () => {
    if (!currentUser || !hairstylistProfile) {
      return;
    }

    if (!STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY === 'pk_test_REPLACE_ME') {
      alert("Stripe n'est pas encore configuré. Veuillez contacter le support.");
      return;
    }

    if (typeof window.Stripe === 'undefined') {
      alert("Impossible d'initialiser Stripe. Veuillez réessayer plus tard.");
      return;
    }

    setActivatingProfile(true);
    try {
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_REPLACE_ME';
      const customerEmail = currentUser.email || '';

      if (!customerEmail) {
        alert('Email manquant. Veuillez vérifier votre compte.');
        setActivatingProfile(false);
        return;
      }

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          customerEmail,
          existingProfileId: hairstylistProfile.id,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // eslint-disable-next-line no-console
        console.error('[SearchPage] createCheckoutSession failed', errorText);
        alert('Erreur lors de la création de la session de paiement. Veuillez réessayer.');
        setActivatingProfile(false);
        return;
      }

      const data = await response.json();
      const { sessionId } = data;

      if (!sessionId) {
        // eslint-disable-next-line no-console
        console.error('[SearchPage] Réponse sans sessionId', data);
        alert('Erreur lors de la création de la session de paiement.');
        setActivatingProfile(false);
        return;
      }

      const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('[SearchPage] redirectToCheckout error', error);
        alert('Erreur lors de la redirection vers Stripe. Veuillez réessayer.');
        setActivatingProfile(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error activating profile:', error);
      alert('Erreur lors de l\'activation. Veuillez réessayer.');
      setActivatingProfile(false);
    }
  };
  
  // Tous les hooks doivent être appelés avant tout return conditionnel
  const [filters, setFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...getDefaultFilters(language), ...parsed };
      }
    } catch (e) {
      // ignore parsing errors and fallback to defaults
    }
    return getDefaultFilters(language);
  });
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SORT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.sortBy || '-';
      }
    } catch (e) {
      // ignore
    }
    return '-'; // Par défaut : tri personnalisé (notes puis dernière modif)
  });
  const [sortOrder, setSortOrder] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SORT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.sortOrder || 'asc';
      }
    } catch (e) {
      // ignore
    }
    return 'asc'; // 'asc' ou 'desc'
  });
  const [viewMode, setViewMode] = useState(() => {
    try {
      const raw = sessionStorage.getItem(VIEW_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.viewMode || 'list';
      }
    } catch (e) {
      // ignore
    }
    return 'list'; // 'list' | 'map'
  });
  const [mapsReady, setMapsReady] = useState(false);
  const [selectedCityCoords, setSelectedCityCoords] = useState(null);
  const [selectedMarkerStylists, setSelectedMarkerStylists] = useState(null);
  const [selectedMarkerTitle, setSelectedMarkerTitle] = useState('');
  const geocoderRef = useRef(null);
  const cityGeocodeCacheRef = useRef({});
  const prevSelectedCityRef = useRef('');
  const prevProviderTypeRef = useRef(null);

  // Valider le type et rediriger si invalide (après tous les hooks)
  const providerType = type && isValidProviderType(type) ? type : DEFAULT_PROVIDER_TYPE;

  useEffect(() => {
    if (type && !isValidProviderType(type)) {
      navigate(`/search/${DEFAULT_PROVIDER_TYPE}`, { replace: true });
    }
  }, [type, navigate]);

  // Réinitialiser les filtres quand le type de prestataire change
  useEffect(() => {
    const prevType = prevProviderTypeRef.current;
    
    // Si le type a changé (et que ce n'est pas le premier rendu)
    if (prevType !== null && prevType !== providerType) {
      // Réinitialiser les filtres mais conserver le pays sélectionné
      const defaultFilters = getDefaultFilters(language);
      setFilters((prev) => {
        const newFilters = {
          ...defaultFilters,
          selectedCountry: prev.selectedCountry || defaultFilters.selectedCountry,
        };
        // Sauvegarder dans sessionStorage avec le pays conservé
        try {
          sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(newFilters));
        } catch (e) {
          // ignore storage errors
        }
        return newFilters;
      });
      
      // Réinitialiser le tri
      setSortBy('-');
      setSortOrder('asc');
      
      // Réinitialiser les coordonnées de la ville
      setSelectedCityCoords(null);
      
      // Réinitialiser la sélection de marker
      setSelectedMarkerStylists(null);
      setSelectedMarkerTitle('');
      
      // Nettoyer le sessionStorage pour tri et marker
      try {
        sessionStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ sortBy: '-', sortOrder: 'asc' }));
        sessionStorage.removeItem(MARKER_STORAGE_KEY);
      } catch (e) {
        // ignore storage errors
      }
    }
    
    // Mettre à jour la référence pour la prochaine fois
    prevProviderTypeRef.current = providerType;
  }, [providerType, language]);


  const serviceCategoryMap = useMemo(
    () =>
      services.reduce((acc, service) => {
        acc[service.code] = service.categoryCode;
        return acc;
      }, {}),
    [services],
  );

  const serviceLabelLookup = useMemo(() => {
    return services.reduce((acc, service) => {
      const code = service.code || service.prestationCode || '';
      const frenchLabel = service.label || service.prestation || '';
      // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
      let label = frenchLabel;
      if (language && language !== 'fr' && service.en) {
        label = service.en;
      } else {
        // Utiliser la traduction si disponible, sinon le label français
        const translatedLabel = t(`services.${code}`, { defaultValue: frenchLabel });
        label = translatedLabel || frenchLabel;
      }
      acc[code] = label;
      return acc;
    }, {});
  }, [services, t, language]);

  const serviceCategoryLookup = useMemo(() => {
    return services.reduce((acc, service) => {
      const categoryCode = service.categoryCode || '';
      const category = categories.find((cat) => cat.categoryCode === categoryCode);
      const frenchCategoryLabel = category?.label || 
        service.category || 
        service.categoryLabel || 
        categoryCode || 
        t('search.otherServices');
      // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
      let categoryLabel = frenchCategoryLabel;
      if (language && language !== 'fr' && category?.en) {
        categoryLabel = category.en;
      } else {
        // Utiliser la traduction si disponible, sinon le label français
        const translatedCategoryLabel = t(`categories.${categoryCode}`, { defaultValue: frenchCategoryLabel });
        categoryLabel = translatedCategoryLabel || frenchCategoryLabel;
      }
      acc[service.code] = {
        label: categoryLabel,
        code: categoryCode || categoryLabel,
      };
      return acc;
    }, {});
  }, [services, categories, t, language]);

  const combinedFilters = useMemo(
    () => ({
      ...filters,
      sortBy,
      hasPhotos: filters.hasPhotos,
      sortOrder,
      selectedCityCoords,
    }),
    [filters, sortBy, sortOrder, selectedCityCoords],
  );

  const {
    hairstylists,
    allHairstylists,
    loading,
    error,
    loadMore,
    hasMore,
    totalCount,
  } = useHairstylists(combinedFilters, serviceCategoryMap, providerType);

  const providerLabel = useMemo(() => {
    const currentLang = language || 'fr';
    const count = typeof totalCount === 'number' ? totalCount : 0;
    const labels = PROVIDER_TYPE_LABELS[providerType];
    const fallback = count !== 1 ? t('search.providers') : t('search.provider');
    if (!labels) {
      return fallback;
    }
    return getProviderTypeLabel(providerType, currentLang, {}, count) || fallback;
  }, [providerType, totalCount, t, language]);

  const shouldShowEmptyProviderCta = useMemo(() => {
    // Pour nails / eyelash / makeup : quand c'est vide, afficher un encart incitant à s'inscrire
    if (loading) return false;
    if (providerType === 'hair') return false;
    return totalCount === 0;
  }, [providerType, totalCount, loading]);

  useEffect(() => {
    const fetchCatalog = async () => {
      setCatalogLoading(true);
      try {
        const [categoriesSnap, servicesSnap] = await Promise.all([
          getDocs(query(collection(db, 'categories'), where('type', '==', providerType))),
          getDocs(query(collection(db, 'services'), where('type', '==', providerType))),
        ]);

        setCategories(
          categoriesSnap.docs
            .map((doc) => doc.data())
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );

        setServices(
          servicesSnap.docs
            .map((doc) => {
              const data = doc.data();
              return {
                ...data,
                code: data.code || data.prestationCode || '',
                label: data.label || data.prestation || '',
              };
            })
            .filter((s) => s.code && s.label)
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load catalog', err);
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchCatalog();
  }, [providerType]);

  useEffect(() => {
    loadGoogleMaps({ requirePlaces: true, language: language || 'fr' })
      .then(() => {
        setMapsReady(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Impossible de charger Google Maps.', error);
      });
  }, [language]);

  useEffect(() => {
    if (mapsReady && !geocoderRef.current && window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [mapsReady]);

  const geocodeCity = useCallback(
    (city) =>
      new Promise((resolve) => {
        if (!city || !geocoderRef.current) {
          resolve(null);
          return;
        }

        if (cityGeocodeCacheRef.current[city]) {
          resolve(cityGeocodeCacheRef.current[city]);
          return;
        }

        geocoderRef.current.geocode({ address: city }, (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location;
            const coords = { lat: location.lat(), lng: location.lng() };
            cityGeocodeCacheRef.current[city] = coords;
            resolve(coords);
          } else {
            resolve(null);
          }
        });
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const city = filters.selectedCity?.trim();
    if (!city) {
      setSelectedCityCoords(null);
      return () => {
        cancelled = true;
      };
    }

    if (!mapsReady) {
      return () => {
        cancelled = true;
      };
    }

    geocodeCity(city).then((coords) => {
      if (!cancelled) {
        setSelectedCityCoords(coords);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [filters.selectedCity, geocodeCity, mapsReady]);

  useEffect(() => {
    const prevCity = prevSelectedCityRef.current;
    // Ne pas changer automatiquement le tri si l'utilisateur a choisi "-" (mode par défaut)
    if (sortBy === '-') {
      prevSelectedCityRef.current = filters.selectedCity;
      return;
    }
    if (filters.selectedCity && filters.selectedCity !== prevCity) {
      setSortBy('distance');
    }
    if (!filters.selectedCity && sortBy === 'distance') {
      setSortBy('rating');
    }
    prevSelectedCityRef.current = filters.selectedCity;
  }, [filters.selectedCity, sortBy]);

  const persistSort = (nextSortBy, nextSortOrder) => {
    try {
      sessionStorage.setItem(
        SORT_STORAGE_KEY,
        JSON.stringify({ sortBy: nextSortBy, sortOrder: nextSortOrder }),
      );
    } catch (e) {
      // ignore storage errors
    }
  };

  const persistViewMode = (nextViewMode) => {
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ viewMode: nextViewMode }));
    } catch (e) {
      // ignore storage errors
    }
  };

  const handleSortChange = (value) => {
    const defaultOrders = {
      '-': 'asc',
      rating: 'desc',
      alphabetical: 'asc',
      createdAt: 'desc',
      distance: 'asc',
    };

    if (value === 'distance' && !selectedCityCoords) {
      // Si distance est sélectionné sans ville, basculer vers rating
      setSortBy('rating');
      setSortOrder(defaultOrders.rating);
      persistSort('rating', defaultOrders.rating);
      return;
    }
    setSortBy(value);
    if (defaultOrders[value]) {
      setSortOrder(defaultOrders[value]);
      persistSort(value, defaultOrders[value]);
    }
  };

  const handleSortOrderToggle = () => {
    setSortOrder((prev) => {
      const next = prev === 'asc' ? 'desc' : 'asc';
      persistSort(sortBy, next);
      return next;
    });
  };


  const persistFilters = (nextFilters) => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(nextFilters));
    } catch (e) {
      // ignore storage errors
    }
  };

  const handleApplyFilters = (nextValues) => {
    setFilters((prev) => {
      const merged = {
        ...prev,
        ...nextValues,
      };
      persistFilters(merged);
      return merged;
    });
  };

  const handleCityChange = useCallback((city) => {
    setFilters((prev) => {
      const merged = { ...prev, selectedCity: city };
      try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(merged));
      } catch (e) {
        // ignore
      }
      return merged;
    });
  }, []);

  const handleMarkerClick = useCallback((stylists, title) => {
    // Afficher les profils PRO en premier dans la liste sous la carte
    const sorted = Array.isArray(stylists)
      ? [...stylists].sort((a, b) => {
          const aPro = (a.accountType || 'FREE') === 'PRO';
          const bPro = (b.accountType || 'FREE') === 'PRO';
          if (aPro && !bPro) return -1;
          if (!aPro && bPro) return 1;
          return 0;
        })
      : stylists;
    setSelectedMarkerStylists(sorted);
    setSelectedMarkerTitle(title);

    // Persister la sélection du marker (pour restauration au retour)
    try {
      const ids = Array.isArray(stylists) ? stylists.map((s) => s.id).filter(Boolean) : [];
      sessionStorage.setItem(
        MARKER_STORAGE_KEY,
        JSON.stringify({ ids, title: title || '' }),
      );
    } catch (e) {
      // ignore storage errors
    }
    
    // Faire défiler la page pour que la carte soit en haut
    // Attendre un peu pour que React ait le temps de rendre la liste
    setTimeout(() => {
      // Chercher l'élément de la carte par data attribute
      const mapContainer = document.querySelector('[data-map-container]');
      if (mapContainer) {
        const yOffset = -20; // Petit offset pour ne pas coller complètement en haut
        const y = mapContainer.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }, 300);
  }, []);

  const handleClearFilters = useCallback((e) => {
    e.stopPropagation(); // Empêcher l'ouverture du menu de filtres
    setFilters((prev) => {
      const cleared = {
        ...getDefaultFilters(language),
        selectedCountry: prev.selectedCountry || getDefaultCountryForLanguage(language),
      };
      try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(cleared));
        sessionStorage.removeItem(MARKER_STORAGE_KEY);
      } catch (err) {
        // ignore
      }
      return cleared;
    });
    setSelectedMarkerStylists(null);
    setSelectedMarkerTitle('');
  }, [language]);

  const resultsLabel = `${totalCount} ${providerLabel} ${totalCount > 1 ? t('search.found') : t('search.foundSingular')}`;

  // Fonction pour vérifier si un filtre est actif (le pays n'est pas considéré comme filtre actif : toujours un pays par défaut)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchText.trim() !== '' ||
      filters.selectedServices.length > 0 ||
      filters.selectedCategories.length > 0 ||
      filters.kidsAccepted ||
      filters.menAccepted ||
      filters.canTravel ||
      filters.hasPhotos ||
      filters.selectedCities.length > 0
    );
  }, [filters]);

  const handleCountryChange = useCallback((country) => {
    setFilters((prev) => {
      const reset = {
        ...getDefaultFilters(language),
        selectedCountry: country || getDefaultCountryForLanguage(language),
      };
      try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(reset));
      } catch (e) {
        // ignore
      }
      return reset;
    });
  }, [language]);

  // Bloquer le scroll de la page quand le panneau de filtres mobile est ouvert
  useEffect(() => {
    if (showFiltersMobile) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [showFiltersMobile]);

  // Restaurer la position de scroll si on revient depuis une fiche coiffeuse
  useEffect(() => {
    if (!loading) {
      const savedY = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      if (savedY) {
        const y = parseInt(savedY, 10);
        if (!Number.isNaN(y)) {
          window.scrollTo(0, y);
        }
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      }
    }
  }, [loading]);

  // Restaurer la sélection de marker (liste sous la carte) si on revient en vue carte
  useEffect(() => {
    if (loading || viewMode !== 'map' || selectedMarkerStylists) {
      return;
    }
    try {
      const raw = sessionStorage.getItem(MARKER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed.ids) ? parsed.ids : [];
      if (!ids.length) {
        sessionStorage.removeItem(MARKER_STORAGE_KEY);
        return;
      }
      let restored = allHairstylists.filter((h) => ids.includes(h.id));
      // Profils PRO en premier (même ordre que au clic sur le marqueur)
      restored = restored.sort((a, b) => {
        const aPro = (a.accountType || 'FREE') === 'PRO';
        const bPro = (b.accountType || 'FREE') === 'PRO';
        if (aPro && !bPro) return -1;
        if (!aPro && bPro) return 1;
        return 0;
      });
      if (restored.length) {
        setSelectedMarkerStylists(restored);
        setSelectedMarkerTitle(parsed.title || '');
      } else {
        // Si on ne retrouve pas les coiffeuses, nettoyer la sélection persistée
        sessionStorage.removeItem(MARKER_STORAGE_KEY);
      }
    } catch (e) {
      // ignore JSON / storage errors
    }
  }, [loading, viewMode, allHairstylists, selectedMarkerStylists]);

  // Résumé simple des filtres actifs pour l'analytics (search_view) — pays exclu (toujours un pays par défaut)
  const activeFiltersSummary = useMemo(() => {
    const summary = [];
    if (filters.selectedCity) summary.push(`city:${filters.selectedCity}`);
    if (filters.selectedCities && filters.selectedCities.length > 0) {
      filters.selectedCities.forEach((c) => summary.push(`city_multi:${c}`));
    }
    if (filters.kidsAccepted) summary.push('kidsAccepted');
    if (filters.menAccepted) summary.push('menAccepted');
    if (filters.canTravel) summary.push('canTravel');
    if (filters.hasPhotos) summary.push('hasPhotos');
    if (filters.searchText?.trim()) summary.push('searchText');
    if (filters.selectedServices && filters.selectedServices.length > 0) {
      summary.push('servicesSelected');
    }
    return summary;
  }, [filters]);

  const providerTypeOptions = useMemo(
    () => [
      { type: 'hair', label: PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR].fr, icon: '/icons/hair.png' },
      { type: 'nails', label: PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS].fr, icon: '/icons/nails.png' },
      { type: 'eyelash', label: PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH].fr, icon: '/icons/eyelash.png' },
      { type: 'makeup', label: PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP].fr, icon: '/icons/makeup.png' },
      { type: 'vendor', label: PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR].fr, icon: '/icons/vendor.png' },
    ],
    [language],
  );

  return (
    <div className={styles.page}>
      {isPendingPayment && hairstylistProfile && (
        <div className={styles.pendingPaymentBanner}>
          <span className={styles.pendingPaymentIcon}>⚠️</span>
          <span className={styles.pendingPaymentText}>
            {t('search.profileNotPublished')}
          </span>
          <button
            type="button"
            className={styles.pendingPaymentLink}
            onClick={handleActivateProfile}
            disabled={activatingProfile}
          >
            {activatingProfile ? t('common.loading') : t('search.activateProfile')}
          </button>
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.layout}>
          <aside
            className={`${styles.sidebar} ${showFiltersMobile ? styles.sidebarVisible : ''}`}
          >
            <FiltersSidebar
              providerType={providerType}
              categories={categories}
              services={services}
              selectedServices={filters.selectedServices}
              selectedCities={filters.selectedCities}
              selectedCity={filters.selectedCity}
              selectedCountry={filters.selectedCountry}
              onCountryChange={handleCountryChange}
              kidsAccepted={filters.kidsAccepted}
              menAccepted={filters.menAccepted}
              canTravel={filters.canTravel}
              hasPhotos={filters.hasPhotos}
              searchText={filters.searchText}
              onSelectedCityChange={handleCityChange}
              onApply={handleApplyFilters}
              onClose={() => setShowFiltersMobile(false)}
              loadingCatalog={catalogLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortByChange={handleSortChange}
              onSortOrderToggle={handleSortOrderToggle}
              canSortByDistance={!!selectedCityCoords}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
            />
          </aside>

          <section className={styles.mainContent}>
            <div className={styles.resultsGrid}>
              <div className={styles.mobileFiltersStack}>
                <div className={styles.mobileFiltersBar}>
                  <div className={styles.mobileCountryWrapper}>
                    <SearchableCountrySelect
                      value={(filters.selectedCountry || getDefaultCountryForLanguage(language)).toUpperCase()}
                      onChange={(code) => handleCountryChange(code ? code.toLowerCase() : getDefaultCountryForLanguage(language))}
                      locale={language || 'fr'}
                      placeholder=""
                      allowEmpty={false}
                      noResultsLabel={t('search.noCountryFound') || 'Aucun pays trouvé'}
                      compactDisplay
                      className={styles.mobileCountrySelect}
                    />
                  </div>
                  <button type="button" onClick={() => setShowFiltersMobile((prev) => !prev)}>
                    {t('search.searchByCityService')}
                    {hasActiveFilters ? (
                      <span className={styles.activeFilterBadge} onClick={handleClearFilters}>
                        {t('search.activeFilter')} ×
                      </span>
                    ) : (
                      <span className={styles.activeFilterBadge} style={{ visibility: 'hidden' }}>{t('search.activeFilter')} ×</span>
                    )}
                  </button>
                </div>

                <div className={styles.providerTypeRowWrapper}>
                  <div className={styles.providerTypeRow}>
                    {providerTypeOptions.map((option) => (
                      <button
                        key={option.type}
                        type="button"
                        className={`${styles.providerTypeButton} ${
                          providerType === option.type ? styles.providerTypeButtonActive : ''
                        }`}
                        onClick={() => navigate(`/search/${option.type}`)}
                      >
                        {providerType === option.type && (
                          <span className={styles.providerTypeHighlight} aria-hidden="true" />
                        )}
                        <span className={styles.providerTypeContent}>
                          <span className={`${styles.providerTypeIconWrapper} ${option.type === 'vendor' ? styles.providerTypeIconWrapperVendor : ''}`}>
                            <img src={option.icon} alt="" className={styles.providerTypeIcon} />
                          </span>
                          <span>{option.label}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className={styles.providerTypeRowFade} aria-hidden="true" />
                </div>
              </div>

              <div className={styles.resultsHeaderRow}>
                <div className={styles.resultsSummary}>
                  {loading ? t('search.searching') : resultsLabel}
                </div>
                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    className={`${styles.viewToggleButton} ${
                      viewMode === 'list' ? styles.viewToggleButtonActive : ''
                    }`}
                    aria-label={t('search.viewList')}
                    onClick={() => {
                      setViewMode('list');
                      persistViewMode('list');
                    }}
                  >
                    <span className={styles.viewToggleIcon} aria-hidden="true">
                      <svg viewBox="0 0 24 24" role="img" focusable="false">
                        <rect x="5" y="6" width="14" height="2" rx="1" />
                        <rect x="5" y="11" width="14" height="2" rx="1" />
                        <rect x="5" y="16" width="14" height="2" rx="1" />
                      </svg>
                    </span>
                    <span className={styles.viewToggleLabel}>{t('search.viewList')}</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewToggleButton} ${
                      viewMode === 'map' ? styles.viewToggleButtonActive : ''
                    }`}
                    aria-label={t('search.viewMap')}
                    onClick={() => {
                      setViewMode('map');
                      persistViewMode('map');
                    }}
                  >
                    <span className={styles.viewToggleIcon} aria-hidden="true">
                      <svg viewBox="0 0 24 24" role="img" focusable="false">
                        <path d="M12 3a5.5 5.5 0 0 0-5.5 5.5c0 4.1 5.5 11.5 5.5 11.5s5.5-7.4 5.5-11.5A5.5 5.5 0 0 0 12 3zm0 8a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
                      </svg>
                    </span>
                    <span className={styles.viewToggleLabel}>{t('search.viewMap')}</span>
                  </button>
                </div>
              </div>

              {shouldShowEmptyProviderCta && (
                <div className={styles.emptyProviderCta} role="note" aria-label={t('search.providerRegistration')}>
                  <div className={styles.emptyProviderCtaText}>
                    <strong>{t('search.youAreProvider', { provider: providerLabel })}</strong> {t('search.signupHere')}
                  </div>
                  <button
                    type="button"
                    className={styles.emptyProviderCtaButton}
                    onClick={() => navigate(`/signup/${providerType}`)}
                  >
                    {t('header.signup')}
                  </button>
                </div>
              )}

              {viewMode === 'list' && (
                <HairstylistList
                  hairstylists={loading ? [] : hairstylists}
                  loading={loading}
                  error={error}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  serviceLookup={serviceLabelLookup}
                        serviceCategoryLookup={serviceCategoryLookup}
                        activeFiltersSummary={activeFiltersSummary}
                />
              )}
              {viewMode === 'map' && !loading && allHairstylists.length > 0 && (
                <>
                  <HairstylistsMap
                    hairstylists={allHairstylists}
                    selectedCityCoords={selectedCityCoords}
                    selectedCountry={filters.selectedCountry || getDefaultCountryForLanguage(language)}
                    onMarkerClick={handleMarkerClick}
                    providerType={providerType}
                  />
                  {selectedMarkerStylists && selectedMarkerStylists.length > 0 && (
                    <div className={styles.markerStylistsList}>
                      <div className={styles.markerStylistsHeader}>
                        <h3>{selectedMarkerTitle}</h3>
                        <button
                          type="button"
                          className={styles.closeMarkerList}
                          onClick={() => {
                            setSelectedMarkerStylists(null);
                            setSelectedMarkerTitle('');
                          }}
                          aria-label={t('common.close')}
                        >
                          ×
                        </button>
                      </div>
                      <HairstylistList
                        hairstylists={selectedMarkerStylists}
                        loading={false}
                        error={null}
                        hasMore={false}
                        loadMore={() => {}}
                        serviceLookup={serviceLabelLookup}
                               serviceCategoryLookup={serviceCategoryLookup}
                               activeFiltersSummary={activeFiltersSummary}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default SearchPage;

